import { Controller } from '@hotwired/stimulus';

export const FREE_LIMIT = 2;

const DEFAULT_MEMBER_TEASER = 'Unlimited generations with membership';

export default class extends Controller {
  static targets = ['dialog', 'usageMeter', 'progressLabel', 'submit'];
  static values = {
    tool: String,
    paidMember: Boolean,
    remaining: Number,
    runLimit: { type: Number, default: FREE_LIMIT },
    requireSignUp: { type: Boolean, default: false },
    signUpUrl: String,
    signInUrl: String,
    memberTeaser: { type: String, default: DEFAULT_MEMBER_TEASER },
    hideMeterUntilStarted: { type: Boolean, default: false },
    suppressUpgrade: { type: Boolean, default: false },
    meterLinkClass: {
      type: String,
      default: 'underline font-medium text-emerald-600 hover:text-emerald-700'
    }
  };

  connect() {
    this.dismissedThisSession = sessionStorage.getItem(this.dismissKey()) === '1';
    this.boundOnSubmitClick = this.onSubmitClick.bind(this);
    if (this.hasSubmitTarget) {
      this.submitTarget.addEventListener('click', this.boundOnSubmitClick);
    }
    this.syncUi(this.effectiveRemaining());
  }

  disconnect() {
    if (this.hasSubmitTarget && this.boundOnSubmitClick) {
      this.submitTarget.removeEventListener('click', this.boundOnSubmitClick);
    }
  }

  open() {
    if (this.paidMemberValue) return;
    if (this.hasDialogTarget) this.dialogTarget.showModal();
  }

  close() {
    if (this.hasDialogTarget) this.dialogTarget.close();
    this.dismissedThisSession = true;
    sessionStorage.setItem(this.dismissKey(), '1');
  }

  handleLimitReached() {
    this.syncUi(0);
    if (!this.dismissedThisSession) this.open();
  }

  onSubmitClick(event) {
    if (!this.isAtLimit()) return;
    event.preventDefault();
    if (this.requireSignUpValue && this.signUpUrlValue) {
      window.location.assign(this.signUpUrlValue);
      return;
    }
    if (this.suppressUpgradeValue) {
      if (this.hasSubmitTarget && this.submitTarget.dataset.paywallAtLimit === 'modal') {
        this.open();
      }
      return;
    }
    if (this.hasSubmitTarget && this.submitTarget.dataset.paywallAtLimit === 'modal') {
      this.open();
      return;
    }
    window.location.assign(this.pricingUrl());
  }

  isAtLimit() {
    return !this.paidMemberValue && this.effectiveRemaining() === 0;
  }

  pricingUrl() {
    return `/pricing?source=generator-${this.toolValue}`;
  }

  applyUsageFromResponse(usage) {
    if (!usage || this.paidMemberValue) return;
    if (typeof usage.remaining === 'number') {
      this.remainingValue = usage.remaining;
      this.syncUi(usage.remaining);
      return;
    }
    if (typeof usage.usage_meter === 'string' && this.hasUsageMeterTarget) {
      this.usageMeterTarget.classList.remove('hidden');
      this.usageMeterTarget.textContent = usage.usage_meter;
    }
  }

  effectiveRemaining() {
    if (this.paidMemberValue) return null;
    const r = this.remainingValue;
    return r < 0 ? this.runLimitValue : r;
  }

  memberTeaserLine() {
    if (this.suppressUpgradeValue) return null;
    const teaser = this.memberTeaserValue;
    return teaser && teaser.length > 0 ? teaser : DEFAULT_MEMBER_TEASER;
  }

  memberTeaserLinkHtml() {
    const text = this.memberTeaserLine();
    if (!text) return '';
    const url = this.escapeAttr(this.pricingUrl());
    const cls = this.escapeAttr(this.meterLinkClassValue);
    return `<a href="${url}" class="${cls}">${this.escapeHtml(text)}</a>`;
  }

  meterPrefixForRemaining(rem) {
    const limit = this.runLimitValue;

    if (this.suppressUpgradeValue) {
      if (rem === limit) return `${limit} comment passes left today`;
      if (rem === 1) return '1 comment pass left today';
      return `${rem} of ${limit} comment passes left today`;
    }

    if (rem === limit) return `${limit} free generations`;
    if (rem === 1) return '1 free generation left';
    return `${rem} of ${limit} free generations left`;
  }

  meterHtmlForRemaining(rem) {
    const link = this.memberTeaserLinkHtml();
    if (!link) return this.meterPrefixForRemaining(rem);
    return `${this.meterPrefixForRemaining(rem)} · ${link}`;
  }

  shouldShowUsageMeter(rem, paid, atLimit) {
    if (!this.hideMeterUntilStartedValue) return true;
    return paid || atLimit || rem < this.runLimitValue;
  }

  syncUi(remaining) {
    const paid = this.paidMemberValue;
    const rem = paid ? null : Math.max(0, remaining ?? 0);
    const atLimit = !paid && rem === 0;
    const showMeter = this.shouldShowUsageMeter(rem, paid, atLimit);

    if (this.hasUsageMeterTarget) {
      this.usageMeterTarget.classList.toggle('hidden', !showMeter);
      if (showMeter) {
        if (paid) {
          this.usageMeterTarget.textContent = this.paidMeterCopy();
        } else if (atLimit && this.requireSignUpValue && this.signUpUrlValue) {
          const url = this.signUpUrlValue;
          this.usageMeterTarget.innerHTML =
            `Free tries used · <a href="${url}" class="underline font-medium text-slate-700 hover:text-slate-900">Sign up free</a> for 2/day`;
        } else if (atLimit && this.suppressUpgradeValue) {
          this.usageMeterTarget.textContent =
            'Daily comment pass limit reached. Resets tomorrow';
        } else if (atLimit) {
          const url = this.pricingUrl();
          this.usageMeterTarget.innerHTML =
            `Free limit reached · <a href="${url}" class="underline font-medium text-slate-700 hover:text-slate-900">Unlock unlimited runs</a>`;
        } else {
          this.usageMeterTarget.innerHTML = this.meterHtmlForRemaining(rem);
        }
      }
    }

    if (this.hasSubmitTarget) {
      const submit = this.submitTarget;
      const labelEl = submit.querySelector('[data-submit-label]');
      const defaultLabel = submit.dataset.submitLabelDefault;

      if (atLimit) {
        submit.disabled = false;
        submit.type = 'button';
        if (this.requireSignUpValue && this.signUpUrlValue) {
          submit.setAttribute('aria-label', 'Free tries used. Sign up free');
          if (labelEl && defaultLabel) {
            labelEl.textContent = submit.dataset.submitLabelSignUp || 'Sign up free';
          }
        } else if (this.suppressUpgradeValue) {
          submit.setAttribute('aria-label', 'Daily comment pass limit reached');
          if (labelEl && defaultLabel) {
            labelEl.textContent = submit.dataset.submitLabelAtLimit || 'Limit reached';
          }
        } else {
          submit.setAttribute('aria-label', 'Free limit reached. Unlock unlimited runs.');
          if (labelEl && defaultLabel) {
            labelEl.textContent = submit.dataset.submitLabelAtLimit || 'Limit reached';
          }
        }
      } else {
        submit.type = 'submit';
        submit.disabled = false;
        submit.removeAttribute('aria-label');
        if (labelEl && defaultLabel) labelEl.textContent = defaultLabel;
      }
    }

    if (this.hasProgressLabelTarget && !paid) {
      const limit = this.runLimitValue;
      const used = limit - rem;
      this.progressLabelTarget.textContent = `${used} / ${limit}`;
    }
  }

  paidMeterCopy() {
    return 'Unlimited generations on this tool with your membership.';
  }

  dismissKey() {
    return `generator_paywall_dismissed_${this.toolValue}`;
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(text) {
    return this.escapeHtml(text).replace(/'/g, '&#39;');
  }
}
